import { collection } from '../common';
import {
  type EditorState,
  type EditorView,
  Plugin,
  PluginKey,
  type PMNode,
} from '../pm';
import { store } from '../store';

export const MIN_TOC_HEADINGS = 2;
export const MAX_TOC_HEADINGS = 100;
const HEADING_WATCH_DEBOUNCE_MS = 250;

export type HeadingItem = {
  pos: number;
  level: number;
  text: string;
};

type HeadingStates = ReadonlyMap<EditorView, HeadingItem[]>;

const $internalHeadings = store.atom<HeadingStates>(new Map());

/** View-keyed heading outline for the TOC rail. */
export const $headings = store.atom((get) => get($internalHeadings));

export function extractHeadings(
  doc: PMNode,
  {
    headingName = 'heading',
    max = MAX_TOC_HEADINGS,
  }: { headingName?: string; max?: number } = {},
): HeadingItem[] {
  const items: HeadingItem[] = [];
  if (!doc) {
    return items;
  }

  doc.descendants((node, pos) => {
    if (items.length >= max) {
      return false;
    }
    if (node.type.name !== headingName) {
      return true;
    }

    const text = node.textContent.replace(/\s+/g, ' ').trim();
    if (text) {
      items.push({
        pos,
        level: Math.min(6, Math.max(1, Number(node.attrs.level) || 1)),
        text,
      });
    }
    return false;
  });

  return items;
}

function headingsEqual(a: HeadingItem[], b: HeadingItem[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    const left = a[i];
    const right = b[i];
    if (!left || !right) {
      return false;
    }
    if (
      left.pos !== right.pos ||
      left.level !== right.level ||
      left.text !== right.text
    ) {
      return false;
    }
  }
  return true;
}

function setHeadingsForView(
  state: EditorState,
  view: EditorView,
  value: HeadingItem[] | undefined,
) {
  const current = store.get(state, $internalHeadings);
  if (value === undefined) {
    if (!current.has(view)) {
      return;
    }
    const next = new Map(current);
    next.delete(view);
    store.set(state, $internalHeadings, next);
    return;
  }

  const existing = current.get(view);
  if (existing && headingsEqual(existing, value)) {
    return;
  }

  const next = new Map(current);
  next.set(view, value);
  store.set(state, $internalHeadings, next);
}

function headingWatchPlugin({ headingName }: { headingName: string }) {
  const pluginKey = new PluginKey('heading-watch');

  return new Plugin({
    key: pluginKey,
    view: (view) => {
      let timer: ReturnType<typeof setTimeout> | null = null;

      const publish = () => {
        timer = null;
        if (view.isDestroyed) {
          return;
        }
        setHeadingsForView(
          view.state,
          view,
          extractHeadings(view.state.doc, { headingName }),
        );
      };

      const schedule = () => {
        if (timer != null) {
          clearTimeout(timer);
        }
        timer = setTimeout(publish, HEADING_WATCH_DEBOUNCE_MS);
      };

      // Seed immediately so the rail can appear without waiting for the
      // trailing debounce on first mount.
      publish();

      return {
        update(_view, prevState) {
          if (view.state.doc.eq(prevState.doc)) {
            return;
          }
          schedule();
        },
        destroy: () => {
          if (timer != null) {
            clearTimeout(timer);
            timer = null;
          }
          setHeadingsForView(view.state, view, undefined);
        },
      };
    },
  });
}

export type HeadingWatchConfig = {
  headingName?: string;
};

/**
 * Publishes a live outline of non-empty headings to `$headings`, keyed by
 * EditorView so multiple editors stay independent.
 */
export function setupHeadingWatch(userConfig?: HeadingWatchConfig) {
  const headingName = userConfig?.headingName ?? 'heading';

  return collection({
    id: 'heading-watch',
    plugin: {
      headingWatch: headingWatchPlugin({ headingName }),
    },
    $headings,
  });
}
